import { IBillingCustomerInvoice } from '../../compiler/billing.interface';
import { validPaymentDate } from './billingAdminFlow';
import { isPaymentInvoiceSelectable } from './billingPaymentSelection';

type Invoice = Pick<IBillingCustomerInvoice, 'uuid' | 'invoice_number' | 'invoice_date' | 'outstanding_amount' | 'has_pending_payment' | 'pending_payment_uuid'>;
export const moneyCents = (amount: number) => Math.round(amount * 100);
const isCentAmount = (amount: number) => Number.isFinite(amount) && amount > 0 && Math.abs(amount * 100 - moneyCents(amount)) < 0.000001;

export const buildPaymentAllocationPreview = (invoices: Invoice[], amount: number) => {
  const totalCents = invoices.reduce((total, row) => total + moneyCents(Number(row.outstanding_amount)), 0);
  const valid = isCentAmount(amount) && moneyCents(amount) <= totalCents && invoices.length > 0;
  let available = valid ? moneyCents(amount) : 0;
  const rows = [...invoices].sort((a, b) =>
    new Date(a.invoice_date).getTime() - new Date(b.invoice_date).getTime() || a.invoice_number.localeCompare(b.invoice_number)
  ).map(row => {
    const outstanding = moneyCents(Number(row.outstanding_amount));
    const allocated = Math.min(outstanding, available);
    available -= allocated;
    return { ...row, allocated: allocated / 100, remaining: (outstanding - allocated) / 100 };
  });
  return {
    valid, rows,
    total: totalCents / 100,
    remaining: (totalCents - (valid ? moneyCents(amount) : 0)) / 100,
    paidCount: rows.filter(row => row.allocated > 0 && row.remaining === 0).length,
    partialCount: rows.filter(row => row.allocated > 0 && row.remaining > 0).length,
    untouchedCount: rows.filter(row => row.allocated === 0).length,
    allocations: rows.filter(row => row.allocated > 0).map(row => ({ invoice_uuid: row.uuid, amount: row.allocated })),
  };
};

export const readCompletePaymentSelection = <T extends Invoice>(data: { selection_version?: number; invoices: T[]; pagination: { total: number } }): T[] => {
  if (data.selection_version !== 1 || data.invoices.length !== data.pagination.total ||
      new Set(data.invoices.map(row => row.uuid)).size !== data.invoices.length) {
    throw new Error('ยังโหลดบิลได้ไม่ครบ กรุณาลองใหม่หรือตรวจรุ่น API ก่อนรับชำระ');
  }
  return data.invoices;
};

/** A freshly revalidated cache must win over a table page fetched earlier. */
export const getSelectedPaymentSnapshot = <T extends Invoice>(ids: string[], cache: Record<string, T>, visible: T[]): T[] => {
  const rows = new Map(visible.map(row => [row.uuid, row]));
  Object.values(cache).forEach(row => rows.set(row.uuid, row));
  return ids.map(id => rows.get(id)).filter((row): row is T => !!row);
};

export const comparePaymentSelection = <T extends Invoice>(selected: T[], current: T[]) => {
  const latest = new Map(current.map(row => [row.uuid, row]));
  const eligible: T[] = [];
  const changed: string[] = [];
  selected.forEach(row => {
    const fresh = latest.get(row.uuid);
    if (!fresh || !isPaymentInvoiceSelectable(fresh)) { changed.push(row.invoice_number); return; }
    eligible.push(fresh);
    if (moneyCents(Number(row.outstanding_amount)) !== moneyCents(Number(fresh.outstanding_amount))) changed.push(row.invoice_number);
  });
  return { eligible, changed };
};

export interface IPaymentEvidenceEntry {
  amount: string;
  transferDate: string;
  transferReference: string;
}
export const validatePaymentEvidenceEntries = (entries: IPaymentEvidenceEntry[], amount: number, required: boolean) => {
  const errors: string[] = [];
  const references = new Set<string>();
  let totalCents = 0;
  entries.forEach((entry, index) => {
    if (!isCentAmount(Number(entry.amount))) errors.push(`หลักฐาน ${index + 1}: ระบุยอดมากกว่า 0 และทศนิยมไม่เกิน 2 ตำแหน่ง`);
    else totalCents += moneyCents(Number(entry.amount));
    if (!validPaymentDate(entry.transferDate)) errors.push(`หลักฐาน ${index + 1}: ระบุวันที่โอนให้ถูกต้อง`);
    const reference = entry.transferReference.trim().toLowerCase();
    if (reference && references.has(reference)) errors.push(`หลักฐาน ${index + 1}: เลขอ้างอิงซ้ำในรายการนี้ กรุณาตรวจว่าเป็นเงินโอนเดียวกันหรือไม่`);
    if (reference) references.add(reference);
  });
  if (required && !entries.length) errors.push('แนบหลักฐานอย่างน้อย 1 ไฟล์ก่อนบันทึก');
  if (required && entries.length && totalCents !== moneyCents(amount)) errors.push('ยอดรวมหลักฐานไม่ตรงกับยอดรับชำระครั้งนี้');
  return { errors, total: totalCents / 100, difference: (moneyCents(amount) - totalCents) / 100,
    multipleDates: new Set(entries.map(entry => entry.transferDate)).size > 1 };
};
